import { observable, action } from 'mobx';
import ApiClient from './ApiClient';
import { createMuiTheme } from '@material-ui/core';
import { inject } from 'mobx-react';

const apiURL = '/api/v1/';
const searchUrlAddition = '/search?searchText=';

export default class CustomerCaseStore extends ApiClient {
  @observable
  allCustomerCases = [];
  @observable
  customerCases = [];
  @observable
  oneCustomerCase = {};
  @observable
  caseLanguage = null;

  @observable
  currentUser = null;
  @observable
  editedUser = null;

  @observable
  customerCaseState = 'pending'; // 'pending' / 'done' / 'error'
  @observable
  oneCustomerCaseState = 'pending'; // 'pending' / 'done' / 'error'
  @observable
  fetchTemplatesState = 'pending'; // 'pending' / 'done' / 'error'
  @observable
  userLoginState = 'pending'; // 'pending' / 'done' / 'error'

  @observable
  carouselOpen = false;
  @observable
  carouselPictureIndex = 0;

  @observable
  newCaseName = '';
  @observable
  newCaseId = '';

  @observable
  username = 'username';
  @observable
  password = 'password';

  @observable
  newCaseReady = false;
  @observable
  removeDialogOpen = false;

  @observable
  templates = {};
  @observable
  templateRenderList = {
    'customer.name': '',
    'customer.logo': '',
    'customer.description': '',
    'customer.industry': '',
    'customer.industryCategories': '',
    customerContact: '',
    customerNeed: '',
    customerComment: '',
    'caseText.header': '',
    'caseText.caption': '',
    'caseText.content': '',
    caseImages: '',
    caseNumbers: '',
    'project.yearStarted': '',
    'project.yearPublished': '',
    'project.duration': '',
    'project.value.days': '',
    'project.value.months': '',
    'project.value.euros': '',
    'project.phases': '',
    'project.devMethods': '',
    'project.technologies': '',
    'project.role': '',
    'project.team': '',
    'button.edit': '',
    'button.remove': '',
    'button.pdf': ''
  };
  @observable
  caseSchema = null;
  @observable
  caseSchemaReady = false;

  constructor() {
    super();

    // Fill the currentUser object
    this.getMe();
    this.fetchSchema();
  }

  //change password variable state
  @action
  changePassword(event) {
    this.password = event.target.value;
  }

  //change username variable state
  @action
  changeUsername(event) {
    this.username = event.target.value;
  }

  @action
  logout() {
    this.setAuthorizationToken();
    this.currentUser = null;
  }

  @action
  userLogin() {
    var { username, password } = this;

    var loginDetails = {
      username,
      password
    };

    return this.api()
      .post('/api/v1/user', loginDetails)
      .then(response => response.data)
      .then(this.userLoginSuccess)
      .catch(this.userLoginError);
  }


  @action
	  userLoginWithKey(key) {
		      return this.api()
		        .post('/api/v1/key', {key})
		        .then(response => response.data)
		        .then(this.userLoginSuccess)
		        .catch(this.userLoginError);
		    }

  @action
  userRegister(user) {
    return this.api()
      .put('/api/v1/user', user)
      .then(response => response.data)
  }

  @action.bound
  userLoginSuccess(userInformation) {
    if (userInformation && userInformation.jwt) {
      this.setAuthorizationToken(userInformation.jwt);
      this.currentUser = userInformation;
      this.userLoginState = 'done';
    } else {
      this.userLoginState = 'error';
    }
  }

  @action.bound
  userLoginError(error) {
    this.userLoginState = 'error';
    console.log(error);
  }

  @action
  getMe() {
    return this.api()
      .get(apiURL + 'me')
      .then(response => response.data)
      .then(user => {
        this.currentUser = user;
        return user;
      });
  }

  @action
  getUsers() {
    return this.api()
      .get(apiURL + "users")
      .then(response => response.data);
  }

  @action
  getUser(id) {
    return this.api()
      .get(apiURL + `users/${id}`)
      .then(response => response.data);
  }

  @action
  updateUser(id, user) {
    return this.api()
      .put(apiURL + `users/${id}`, {user})
      .then(response => response.data);
  }

  @action
  removeUser(id) {
    return this.api()
      .delete(apiURL + `users/${id}`)
      .then(response => response.data);
  }

  // Fetch schema
  @action
  fetchSchema() {
    this.api()
      .get(apiURL + "schema")
      .then(response => response.data)
      .then(schema => {
        this.caseSchema = schema;
        this.caseSchemaReady = true;

        return schema;
      });
  }

  // Fetch all Customer Cases
  @action
  fetchCustomerCases(search) {
    this.customerCaseState = 'pending';
    this.api()
      .get(apiURL + "cases" + searchUrlAddition + search)
      .then(response => response.data)
      .then(this.fetchCustomerCasesSuccess)
      .catch(this.fetchCustomerCasesError);
  }

  @action.bound
  fetchCustomerCasesSuccess(customerCases) {
    this.customerCases = customerCases;
    this.customerCaseState = 'done';
  }

  @action.bound
  fetchCustomerCasesError(error) {
    this.customerCaseState = 'error';
  }

  // toggle case view picture carousel open and closed
  @action
  carouselToggle(StartPictureIndex) {
    this.carouselOpen = this.carouselOpen ? false : true;
    this.carouselPictureIndex = StartPictureIndex ? StartPictureIndex : 0;
  }

  //toggle remove case dialog open and closed
  @action
  toggleRemoveDialogOpen() {
    this.removeDialogOpen = this.removeDialogOpen ? false : true;
  }

  // send request to generate pdf in the backend API based on a supplied customer case ID
  @action
  fetchPdf(id, templateId, language) {
    this.api()
      .get(`/api/v1/cases/pdf/${id}/${templateId}/${language}`, {
        responseType: 'blob' // important
      }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'file.pdf');
      document.body.appendChild(link);
      link.click();
    });
  }

  @action
  getTemplates() {
    return this.api()
      .get(apiURL + 'templates')
      .then(response => response.data);
  }

  @action
  getTemplate(id) {
    return this.api()
      .get(apiURL + `templates/${id}`)
      .then(response => response.data);

  }
  @action
  createTemplate(template) {
    return this.api()
      .post(apiURL + 'templates', {template})
      .then(response => response.data);
  }

  @action
  updateTemplate(id, template) {
    return this.api()
      .put(apiURL + `templates/${id}`, {template})
      .then(response => response.data);
  }

  @action
  removeTemplate(id) {
    return this.api()
      .delete(apiURL + `templates/${id}`)
      .then(response => response.data)
  }

  // fetch case view render blacklist values from backend API
  @action
  fetchTemplates(input) {
    console.log(input);
    this.fetchTemplatesState = 'pending';
    this.api()
      .get(`/api/v1/templates/${input}`)
      .then(response => response.data)
      .then(this.fetchTemplatesSuccess)
      .catch(this.fetchTemplatesError);
  }

  @action.bound
  fetchTemplatesSuccess(templates) {
    console.log(templates);
    this.templates = templates;
    this.fetchTemplatesState = 'done';
  }

  @action.bound
  fetchTemplatesError(error) {
    this.fetchTemplatesState = 'error';
    console.log(error);
  }

  //switch applied case view render blacklist
  @action
  changeTemplateMode(templateType, input) {
    if (templateType === 'viewTemplate') {
      this.templates['viewTemplates'].map(template => {
        if (template.templateName == input) {
          this.templateRenderList = template;
        }
      });
    }
  }

  // Fetch Customer Case by _id
  @action
  fetchOneCustomerCase(id, language) {
    console.log(id);
    console.log(language);
    this.oneCustomerCaseState = 'pending';
    this.caseLanguage = language;
    this.api()
      .get(`/api/v1/cases/id/${id}`)
      .then(response => response.data)
      .then(this.fetchOneCustomerCaseSuccess)
      .catch(this.fetchOneCustomerCaseError);
  }

  @action.bound
  fetchOneCustomerCaseSuccess(customerCases) {
    this.oneCustomerCase = customerCases;
  
    if (this.oneCustomerCase.translations.find(o => o.lang === this.caseLanguage) === undefined) {
      this.createCustomerCaseTranslation(this.oneCustomerCase._id, this.caseLanguage);
    } else {
      this.formDataState = 'done';
    }

    this.oneCustomerCaseState = 'done';
  }

  @action
  createCustomerCaseTranslation(caseId, lang) {
    return this.api()
      .post(`/api/v1/cases/${caseId}/translations`, {
        lang
      }).then(() => {
        return this.fetchOneCustomerCase(caseId, lang);
      })
  }

  @action.bound
  fetchOneCustomerCaseError(error) {


	  this.oneCustomerCaseState = 'error';
  }

  // @TODO there is a bug where you create a new case while in case view view, refresh is required to view the new empty reference
  //create new customer case
  @action
  createNewCustomerCase(callback) {
    //this.formDataState = 'pending';
    console.log('createNewCase');
    this.api()
      .post('/api/v1/cases')
      .then(response => response.data)
      .then(callback)
      .catch(this.newCustomerCaseError);
  }

  @action.bound
  newCustomerCaseError(error) {
    console.log(error);
  }

  // remove customer case
  @action
  removeCustomerCase(caseId) {
    console.log('removeCustomerCase');
    this.api()
      .delete(`/api/v1/cases/${caseId}`)
      .then(response => response.data)
      .then(this.removeCustomerCaseSuccess)
      .catch(this.removeCustomerCaseError);
  }

  @action.bound
  removeCustomerCaseSuccess() {
    console.log('removeSuccess');
  }

  @action.bound
  removeCustomerCaseError(error) {
    console.log(error);
  }
}



// WEBPACK FOOTER //
// ./src/store/CustomerCaseStore.js